/* eslint-disable no-undef */
import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Modal, Table } from 'semantic-ui-react';
import { toast, ToastContainer } from 'react-toastify';
import Select, { Creatable } from 'react-select';

import Heading from '../../components/Heading';
import DragAndDrop from '../../components/DragAndDrop';
import Pagination from '../../components/Pagination';
import CountDisplay from '../../components/CountDisplay';
import ButtonContainer from '../../components/ButtonContainer';
import Dropdown from '../../components/Dropdown/Dropdown';
import Loader from '../../components/Loader';
import Button from '../../components/Button';

import user from '../../store/user';
import { adminFeatureMapping } from '../../models/adminFeatureMapping';
import fetchNTCPremiumJobs from '../../services/fetchNTCPremiumJobs';
import fetchNTCPremiumJobAssignees from '../../services/fetchNTCPremiumJobAssignees';
import fetchNTCPremiumJobCustIds from '../../services/fetchNTCPremiumJobCustIds';
import uploadNTCPremiumJobsCsv from '../../services/uploadNTCPremiumJobsCsv';
import updateNTCPremiumJob from '../../services/updateNTCPremiumJob';
import bulkAssignNTCPremiumJobs from '../../services/bulkAssignNTCPremiumJobs';
import fetchNTCPremiumJobRejectionTags from '../../services/fetchNTCPremiumJobRejectionTags';
import createNTCPremiumJobRejectionTag from '../../services/createNTCPremiumJobRejectionTag';
import updateNTCPremiumJobRejectionTags from '../../services/updateNTCPremiumJobRejectionTags';
import fetchNTCPremiumJobRejectionStats from '../../services/fetchNTCPremiumJobRejectionStats';

import './styles.scss';

const PAGE_CONTENT = 20;
const CURRENT_PLATFORM = process.env.REACT_APP_PLATFORM;

const DEFAULT_BRANCH_OPTION = { id: '', name: 'All Branches' };
const DEFAULT_ASSIGNEE_OPTION = { id: '', name: 'All Assignees' };
const UNASSIGNED_OPTION = { id: 'unassigned', name: 'Unassigned' };
const DEFAULT_PERMISSIONS = { role: '', showAssignedToFilter: false, canAssign: false };

const JOB_STATUS_OPTIONS = [
    { id: '', name: 'All Status' },
    { id: 'not-posted', name: 'Not Posted' },
    { id: 'pending', name: 'Pending' },
    { id: 'published', name: 'Published' },
    { id: 'rejected', name: 'Rejected' },
];

const ICP_OPTIONS = [
    { id: '', name: 'All Companies' },
    { id: '1', name: 'ICP Companies' },
    { id: '0', name: 'Non-ICP Companies' },
];

const STATUS_BADGE_LABELS = {
    'not-posted': 'Not Posted',
    'pending': 'Pending',
    'published': 'Published',
    'updated-published': 'Updated Live',
    'unpublished': 'Unpublished',
    'updated-unpublished': 'Upd. Unpublished',
    'rejected': 'Rejected',
};

const STATUS_BADGE_DESCRIPTIONS = {
    'not-posted': "Hasn't been posted yet",
    'pending': 'Posted, awaiting review',
    'published': 'Live on the platform',
    'updated-published': 'Live with pending edits',
    'unpublished': 'Was live, now taken down',
    'updated-unpublished': 'Unpublished with pending edits',
    'rejected': 'Rejected by the platform',
};

const TAG_PILL_COLORS = {
    background: '#eef7ef',
    border: '#b9d8bf',
    text: '#2f6f3e',
    removeHover: '#d8ecdd'
};

const DEFAULT_REJECTION_TAG_NAMES = [
    'Duplicate job',
    'Link expired',
    'Less salary',
    'Position already closed',
    'Recruiter details unavailable',
    'Recruiter details incorrect',
    'Job received enough responses',
    'Recruiter not responding',
    'Position on hold',
    'Need approval from seniors',
    'Not allowed to use other platforms',
    'Already live on iimjobs',
    'Already live on hirist',
    'Client did not post the job',
    'Not satisfied with previous experience',
    'Not interested',
    'Not relevant for iimjobs'
];

const DEFAULT_REJECTION_TAG_OPTIONS = DEFAULT_REJECTION_TAG_NAMES.map(name => ({
    value: name,
    label: name,
    usageCount: 0
}));

const getTagName = (tag) => {
    if (!tag) return '';
    if (typeof tag === 'string') return tag;
    return tag.tagName || tag.tag_name || tag.name || tag.label || '';
};

const getTagId = (tag) => {
    if (!tag) return '';
    if (typeof tag === 'string') return tag;
    return tag.id || tag.tagId || tag.tag_id || tag.value || getTagName(tag);
};

const getTagUsageCount = (tag) => {
    if (!tag) return 0;
    return tag.usageCount || tag.usage_count || tag.count || 0;
};

const formatTagOption = tag => ({
    value: getTagId(tag),
    label: getTagName(tag),
    usageCount: getTagUsageCount(tag)
});

const mergeTagOptions = (apiTags = []) => {
    const options = [...DEFAULT_REJECTION_TAG_OPTIONS];

    apiTags.forEach((tag) => {
        const option = formatTagOption(tag);
        if (!option.label) return;

        const existingIndex = options.findIndex(item =>
            item.value === option.value || item.label.toLowerCase() === option.label.toLowerCase()
        );

        if (existingIndex >= 0) {
            options[existingIndex] = { ...options[existingIndex], ...option };
        } else {
            options.push(option);
        }
    });

    return options;
};

const getRowTags = (row) =>
    row.rejectionTags || row.rejection_tags || row.rejectionReasons || row.rejection_reasons || [];

const getSelectedTagIds = (selectedTags = []) => selectedTags
    .map(item => item.value)
    .filter(value => value !== null && value !== undefined && value !== '');

const getSelectedTagOptions = (row, tagOptions) => {
    const tags = getRowTags(row);
    if (!Array.isArray(tags)) return [];

    return tags.map((tag) => {
        const tagId = getTagId(tag);
        const tagName = getTagName(tag);
        const matchedOption = tagOptions.find(option =>
            option.value === tagId || option.label.toLowerCase() === tagName.toLowerCase()
        );
        return matchedOption || formatTagOption(tag);
    }).filter(item => item.label);
};

function CommentInput({ initialValue, onSave, disabled }) {
    const [value, setValue] = React.useState(initialValue || '');

    React.useEffect(() => { setValue(initialValue || ''); }, [initialValue]);

    return (
        <textarea
            className="ntc-premium-jobs__comment-input"
            placeholder="Add a comment…"
            value={value}
            onChange={e => setValue(e.target.value)}
            onBlur={() => { if (value !== (initialValue || '')) onSave(value); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSave(value); e.target.blur(); } }}
            rows={2}
            disabled={disabled}
        />
    );
}

const makeSelectStyles = (minWidth = 200) => ({
    control: styles => ({
        ...styles,
        borderColor: '#cfcfcf',
        boxShadow: 'none',
        minHeight: '36px',
        minWidth,
        ':hover': { borderColor: '#a7a7a7' }
    }),
    valueContainer: styles => ({ ...styles, padding: '2px 8px' }),
    multiValue: styles => ({
        ...styles,
        alignItems: 'center',
        backgroundColor: TAG_PILL_COLORS.background,
        border: `1px solid ${TAG_PILL_COLORS.border}`,
        borderRadius: '12px'
    }),
    multiValueLabel: styles => ({
        ...styles,
        color: TAG_PILL_COLORS.text,
        fontWeight: 700,
        maxWidth: 'none',
        overflow: 'visible',
        textOverflow: 'clip',
        whiteSpace: 'normal',
    }),
    multiValueRemove: styles => ({
        ...styles,
        color: TAG_PILL_COLORS.text,
        ':hover': { backgroundColor: TAG_PILL_COLORS.removeHover, color: TAG_PILL_COLORS.text }
    }),
    menuPortal: styles => ({ ...styles, zIndex: 9999 }),
    menu: styles => ({ ...styles, zIndex: 9999 })
});

const tagSelectStyles = makeSelectStyles(280);
const inlineTagSelectStyles = makeSelectStyles(0);
const custIdSelectStyles = makeSelectStyles(220);

const assigneeSelectStyles = {
    container: styles => ({ ...styles, width: '100%' }),
    control: styles => ({
        ...styles,
        borderColor: '#cfcfcf',
        boxShadow: 'none',
        cursor: 'text',
        fontSize: '12px',
        minHeight: '28px',
        ':hover': { borderColor: '#a7a7a7' }
    }),
    menu: styles => ({ ...styles, fontSize: '13px', marginTop: 4, zIndex: 9999 }),
    menuPortal: styles => ({ ...styles, zIndex: 9999 }),
    option: styles => ({ ...styles, fontSize: '13px', padding: '8px 12px' }),
    valueContainer: styles => ({ ...styles, padding: '0 6px' }),
};

const getSelectMenuPortalTarget = () =>
    typeof document === 'undefined' ? null : document.body;

const getErrorMessage = (error, fallback) => {
    if (error && error.response && error.response.data && error.response.data.message)
        return error.response.data.message;
    if (error && error.data && error.data.message) return error.data.message;
    if (error && error.message) return error.message;
    return fallback;
};

const formatCountRange = (pageNumber, listLength, totalCount) => {
    if (!totalCount) return { start: 0, end: 0 };
    const start = ((pageNumber - 1) * PAGE_CONTENT) + 1;
    const end = start + listLength - 1;
    return { start, end };
};

const renderLabelValue = (label, value) => (
    <div>
        <strong>{label}</strong>: {value || 'N/A'}
    </div>
);

const normalizePriority = (priority) => {
    if (priority === null || priority === undefined || priority === '') return null;
    const str = String(priority).toUpperCase();
    if (str.startsWith('P')) return str;
    const num = parseInt(str, 10);
    if (!isNaN(num)) return `P${num}`;
    return str;
};

const getPriorityColorSuffix = (normalized) => {
    const known = ['p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6'];
    const key = normalized.toLowerCase();
    if (known.includes(key)) return key;
    const num = parseInt(normalized.replace(/^P/i, ''), 10);
    if (!isNaN(num) && num > 6) return 'phigh';
    return 'p3';
};

const renderPriorityBadge = (priority) => {
    const normalized = normalizePriority(priority);
    if (!normalized) return null;
    return (
        <span className={`ntc-premium-jobs__priority-badge ntc-premium-jobs__priority-badge--${getPriorityColorSuffix(normalized)}`}>
            {normalized}
        </span>
    );
};

const renderIcpBadge = (icp) => {
    if (Number(icp) !== 1) return null;
    return (
        <span className="ntc-premium-jobs__icp-badge ntc-premium-jobs__icp-badge--icp">
            ICP
        </span>
    );
};

const renderJobPublishStatusBadge = (status) => {
    const key = status || 'not-posted';
    const label = STATUS_BADGE_LABELS[key] || key;
    const description = STATUS_BADGE_DESCRIPTIONS[key] || '';
    return (
        <span className="ntc-premium-jobs__status-wrap">
            <span className={`ntc-premium-jobs__status-badge ntc-premium-jobs__status-badge--${key}`}>
                {label}
            </span>
            {description && (
                <span className="ntc-premium-jobs__status-tooltip">{description}</span>
            )}
        </span>
    );
};

const DEFAULT_STATS = {
    totalJobsPosted: 0,
    postedToday: 0,
    postedThisWeek: 0,
    totalPending: 0,
    priorityCounts: {},
    statusCounts: {},
    tags: []
};

const NTCPremiumJobs = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [adminId, setAdminId] = useState(null);
    const [hasAccess, setHasAccess] = useState(true);
    const [isManager, setIsManager] = useState(false);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [statsModalOpen, setStatsModalOpen] = useState(false);
    const [rows, setRows] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [branch, setBranch] = useState('');
    const [branches, setBranches] = useState([]);
    // jobId = the value shown in the Job ID column (row.jobId / Naukri reference ID)
    const [jobId, setJobId] = useState('');
    // custIdFilter = array of {value, label} objects selected from the cust-ID multi-select
    const [custIdFilter, setCustIdFilter] = useState([]);
    const [custIdOptions, setCustIdOptions] = useState([]);
    const [jobStatus, setJobStatus] = useState('');
    const [icp, setIcp] = useState('');
    const [postDateFrom, setPostDateFrom] = useState('');
    const [postDateTo, setPostDateTo] = useState('');
    const [rejectionTagFilter, setRejectionTagFilter] = useState([]);
    const [assignedTo, setAssignedTo] = useState('');
    const [, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [assignees, setAssignees] = useState([]);
    const [rejectionTagOptions, setRejectionTagOptions] = useState(DEFAULT_REJECTION_TAG_OPTIONS);
    const [rowRejectionTags, setRowRejectionTags] = useState({});
    const [rejectionStats, setRejectionStats] = useState(DEFAULT_STATS);
    const [rowUpdateMap, setRowUpdateMap] = useState({});
    const [checkedIds, setCheckedIds] = useState([]);
    const [selectAllMode, setSelectAllMode] = useState(false);
    const [bulkAssignTo, setBulkAssignTo] = useState('');

    const branchOptions = useMemo(() => [
        DEFAULT_BRANCH_OPTION,
        ...branches.map(item => ({ id: item, name: item }))
    ], [branches]);

    const assigneeOptions = useMemo(() => [
        DEFAULT_ASSIGNEE_OPTION,
        UNASSIGNED_OPTION,
        ...assignees.map(item => ({ id: item.id, name: item.name }))
    ], [assignees]);

    const assigneeNameMap = useMemo(() =>
        assignees.reduce((acc, item) => { acc[item.id] = item.name; return acc; }, {}),
    [assignees]);

    const visibleMetricTags = useMemo(() => {
        if (rejectionStats.tags && rejectionStats.tags.length) {
            return rejectionStats.tags.map(tag => formatTagOption(tag)).filter(tag => tag.usageCount > 0);
        }
        return rejectionTagOptions.filter(item => item.usageCount > 0);
    }, [rejectionStats.tags, rejectionTagOptions]);

    const getPostJobRedirectUrl = (row) => {
        const params = [
            ['ntcPremiumJobId', row.id],
            ['returnTo', '/ntc-premium-jobs'],
            ['refrenceId', row.jobId],
        ].filter(([, value]) => value !== null && value !== undefined && value !== '');
        if (!params.length) return '/postjob';
        return `/postjob?${params.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join('&')}`;
    };

    const fetchList = async (
        nextPageNumber = 1,
        nextBranch = branch,
        nextJobId = jobId,
        nextAssignedTo = assignedTo,
        nextRejectionTagFilter = rejectionTagFilter,
        nextCustIdFilter = custIdFilter,
        nextJobStatus = jobStatus,
        nextPostDateFrom = postDateFrom,
        nextPostDateTo = postDateTo,
        nextIcp = icp
    ) => {
        if (!adminId) return;

        setLoading(true);
        try {
            const selectedRejectionTagIds = getSelectedTagIds(nextRejectionTagFilter);
            const params = {
                pageNumber: nextPageNumber,
                pageContent: PAGE_CONTENT,
                branch: nextBranch,
            };
            if (nextAssignedTo) params.assignedTo = nextAssignedTo;
            if (nextJobId) params.jobId = nextJobId;
            if (selectedRejectionTagIds.length) params.rejectionTagIds = selectedRejectionTagIds.join(',');
            if (nextCustIdFilter.length) params.custIds = nextCustIdFilter.map(o => o.value).join(',');
            if (nextJobStatus) params.jobPublishStatus = nextJobStatus;
            if (nextPostDateFrom) params.postDateFrom = nextPostDateFrom;
            if (nextPostDateTo) params.postDateTo = nextPostDateTo;
            if (nextIcp !== '') params.icp = nextIcp;

            const response = await fetchNTCPremiumJobs(adminId, params);
            if (!response || response.status !== 'success') throw response;

            const data = response.data || {};
            const list = (data.list || []).slice().sort((a, b) => {
                const pa = normalizePriority(a.priority);
                const pb = normalizePriority(b.priority);
                const na = pa ? parseInt(pa.replace(/^P/i, ''), 10) : 9999;
                const nb = pb ? parseInt(pb.replace(/^P/i, ''), 10) : 9999;
                if (na !== nb) return na - nb;
                const da = new Date(a.jobPostingDate || a.posted_date || a.postedDate || 0).getTime();
                const db = new Date(b.jobPostingDate || b.posted_date || b.postedDate || 0).getTime();
                return db - da;
            });
            setRows(list);
            setTotalCount(data.count || 0);
            setBranches(data.branches || []);
            setPermissions(data.permissions || DEFAULT_PERMISSIONS);
            setRowRejectionTags(list.reduce((acc, item) => {
                acc[item.id] = getSelectedTagOptions(item, rejectionTagOptions);
                return acc;
            }, {}));

            if (data.rejectionStats || data.stats) {
                const stats = data.rejectionStats || data.stats || {};
                setRejectionStats({
                    totalJobsPosted: stats.totalJobsPosted || stats.total_jobs_posted || stats.totalPosted || stats.posted_count || stats.postedCount || 0,
                    postedToday: stats.postedToday || stats.posted_today || 0,
                    postedThisWeek: stats.postedThisWeek || stats.posted_this_week || 0,
                    totalPending: stats.totalPending || stats.total_pending || 0,
                    priorityCounts: stats.priorityCounts || stats.priority_counts || {},
                    statusCounts: stats.statusCounts || stats.status_counts || {},
                    tags: stats.tags || stats.rejectionTags || stats.rejection_tags || []
                });
            }

            setPageNumber(nextPageNumber);
            fetchStats(nextBranch, nextJobId, nextAssignedTo, nextRejectionTagFilter, nextCustIdFilter, nextJobStatus, nextPostDateFrom, nextPostDateTo, nextIcp);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to fetch NTC premium jobs'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        (async () => {
            const profile = await user.getProfile();
            setAdminId(profile.id);
            const managerAccess = user.checkFeatureAccess(adminFeatureMapping.NTC_PREMIUM_JOBS_MANAGER);
            const assigneeAccess = user.checkFeatureAccess(adminFeatureMapping.NTC_PREMIUM_JOBS_ASSIGNEE);
            setHasAccess(managerAccess || assigneeAccess);
            setIsManager(managerAccess);
        })();
    }, []);

    useEffect(() => {
        if (adminId && hasAccess) {
            fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo);
        } else if (adminId && !hasAccess) {
            setLoading(false);
        }
    }, [adminId, hasAccess]);

    useEffect(() => {
        if (!(adminId && hasAccess)) return;
        (async () => {
            const response = await fetchNTCPremiumJobRejectionTags(adminId);
            if (response && response.status === 'success') {
                const tags = response.data && response.data.list ? response.data.list : response.data || [];
                setRejectionTagOptions(mergeTagOptions(Array.isArray(tags) ? tags : []));
            }
        })();
    }, [adminId, hasAccess]);

    useEffect(() => {
        if (!(adminId && isManager)) return;
        (async () => {
            const response = await fetchNTCPremiumJobAssignees(adminId);
            if (response && response.status === 'success') setAssignees(response.data || []);
        })();
    }, [adminId, isManager]);

    // Requires backend: GET /admin/{id}/ntc-premium-jobs/cust-ids → { data: [123, 456, ...] }
    useEffect(() => {
        if (!(adminId && hasAccess)) return;
        (async () => {
            const response = await fetchNTCPremiumJobCustIds(adminId);
            if (response && response.status === 'success') {
                const ids = response.data || [];
                setCustIdOptions(
                    (Array.isArray(ids) ? ids : [])
                        .filter(id => id !== null && id !== undefined && id !== '')
                        .map(id => ({ value: String(id), label: String(id) }))
                );
            }
        })();
    }, [adminId, hasAccess]);

    const fetchStats = async (
        nextBranch = branch,
        nextJobId = jobId,
        nextAssignedTo = assignedTo,
        nextRejectionTagFilter = rejectionTagFilter,
        nextCustIdFilter = custIdFilter,
        nextJobStatus = jobStatus,
        nextPostDateFrom = postDateFrom,
        nextPostDateTo = postDateTo,
        nextIcp = icp
    ) => {
        if (!adminId) return;

        const selectedRejectionTagIds = getSelectedTagIds(nextRejectionTagFilter);
        const params = { branch: nextBranch };
        if (nextAssignedTo) params.assignedTo = nextAssignedTo;
        if (nextJobId) params.jobId = nextJobId;
        if (selectedRejectionTagIds.length) params.rejectionTagIds = selectedRejectionTagIds.join(',');
        if (nextCustIdFilter.length) params.custIds = nextCustIdFilter.map(o => o.value).join(',');
        if (nextJobStatus) params.jobPublishStatus = nextJobStatus;
        if (nextPostDateFrom) params.postDateFrom = nextPostDateFrom;
        if (nextPostDateTo) params.postDateTo = nextPostDateTo;
        if (nextIcp !== '') params.icp = nextIcp;

        const response = await fetchNTCPremiumJobRejectionStats(adminId, params);
        if (response && response.status === 'success') {
            const data = response.data || {};
            setRejectionStats({
                totalJobsPosted: data.totalJobsPosted || data.total_jobs_posted || data.totalPosted || data.posted_count || data.postedCount || 0,
                postedToday: data.postedToday || data.posted_today || 0,
                postedThisWeek: data.postedThisWeek || data.posted_this_week || 0,
                totalPending: data.totalPending || data.total_pending || 0,
                priorityCounts: data.priorityCounts || data.priority_counts || {},
                statusCounts: data.statusCounts || data.status_counts || {},
                tags: data.tags || data.rejectionTags || data.rejection_tags || []
            });
        }
    };

    const handleUpload = async (file) => {
        if (!adminId) { toast.error('Admin ID is not available'); return; }

        const formData = new FormData();
        formData.append('csv', file);
        setUploading(true);
        try {
            const response = await uploadNTCPremiumJobsCsv(adminId, formData);
            if (response && response.status === 'success') {
                const data = response.data || {};
                toast.success(`Uploaded successfully. Jobs: inserted ${data.insertedCount || 0}, updated ${data.updatedCount || 0}.`);
                setSelectedFile(null);
                setUploadModalOpen(false);
                await fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo, icp);
                return;
            }
            throw response;
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to upload NTC premium jobs CSV'));
            setSelectedFile(null);
        } finally {
            setUploading(false);
        }
    };

    const setRowUpdating = (key, value) =>
        setRowUpdateMap(prev => ({ ...prev, [key]: value }));

    const handlePageChange = (nextPageNumber) => {
        setCheckedIds([]);
        setSelectAllMode(false);
        fetchList(nextPageNumber, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo, icp);
    };

    const handleCheckboxChange = (rowId) => {
        setSelectAllMode(false);
        setCheckedIds(prev => prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]);
    };

    const handleSelectAll = () => {
        if (selectAllMode || checkedIds.length > 0) {
            setSelectAllMode(false);
            setCheckedIds([]);
        } else {
            // Select all matching records across all pages
            setCheckedIds(rows.map(r => r.id));
            setSelectAllMode(true);
        }
    };

    const handleBulkAssign = async (assignTo) => {
        if (!assignTo) return;
        if (!selectAllMode && !checkedIds.length) return;
        try {
            if (selectAllMode) {
                // Requires backend: POST /admin/{id}/ntc-premium-jobs/bulk-assign
                const response = await bulkAssignNTCPremiumJobs(adminId, {
                    assignedTo: Number(assignTo),
                    filters: {
                        branch,
                        jobId,
                        custIds: custIdFilter.map(o => o.value).join(','),
                        jobStatus,
                        icp,
                        postDateFrom,
                        postDateTo,
                        assignedTo: isManager ? assignedTo : adminId
                    }
                });
                if (!response || response.status !== 'success') throw response;
                await fetchList(pageNumber, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo, icp);
            } else {
                await Promise.all(checkedIds.map(rowId => updateNTCPremiumJob(adminId, rowId, { assignedTo: assignTo })));
                setRows(prev => prev.map(row =>
                    checkedIds.includes(row.id)
                        ? { ...row, assignedTo: Number(assignTo), assignedToName: assigneeNameMap[assignTo] || '' }
                        : row
                ));
            }
            setCheckedIds([]);
            setSelectAllMode(false);
            setBulkAssignTo('');
            toast.success('Assigned successfully');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to assign jobs'));
        }
    };

    const handleBranchChange = (event) => {
        const nextBranch = event.target.value;
        setBranch(nextBranch);
        fetchList(1, nextBranch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo);
    };

    const handleAssignedToFilterChange = (event) => {
        const nextAssignedTo = event.target.value;
        setAssignedTo(nextAssignedTo);
        fetchList(1, branch, jobId, nextAssignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo);
    };

    const handleJobStatusChange = (event) => {
        const nextStatus = event.target.value;
        setJobStatus(nextStatus);
        fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, nextStatus, postDateFrom, postDateTo);
    };

    const handleIcpChange = (event) => {
        const nextIcp = event.target.value;
        setIcp(nextIcp);
        fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo, nextIcp);
    };

    const handleJobIdChange = (event) => setJobId(event.target.value);

    const handleJobIdSearch = (event) => {
        if (event.key === 'Enter')
            fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo);
    };

    const handleCustIdFilterChange = (selectedOptions) => {
        const next = selectedOptions || [];
        setCustIdFilter(next);
        fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, next, jobStatus, postDateFrom, postDateTo);
    };

    const handlePostDateFromChange = (event) => setPostDateFrom(event.target.value);
    const handlePostDateToChange = (event) => setPostDateTo(event.target.value);

    const handleClearFilters = () => {
        setBranch('');
        setJobId('');
        setCustIdFilter([]);
        setAssignedTo('');
        setJobStatus('');
        setIcp('');
        setPostDateFrom('');
        setPostDateTo('');
        setRejectionTagFilter([]);
        fetchList(1, '', '', '', [], [], '', '', '', '');
    };

    const handleRejectionTagFilterChange = (selectedOptions) => {
        const next = selectedOptions || [];
        setRejectionTagFilter(next);
        fetchList(1, branch, jobId, assignedTo, next, custIdFilter, jobStatus, postDateFrom, postDateTo);
    };

    const handlePostJob = (row) => { window.location.href = getPostJobRedirectUrl(row); };

    const saveRowRejectionTags = async (rowId, selectedOptions) => {
        const updateKey = `${rowId}-rejection-tags`;
        setRowUpdating(updateKey, true);
        try {
            const selectedTags = selectedOptions || [];
            const response = await updateNTCPremiumJobRejectionTags(adminId, rowId, {
                tagIds: getSelectedTagIds(selectedTags)
            });
            if (!response || response.status !== 'success') throw response;
            setRows(prev => prev.map(row => row.id === rowId ? {
                ...row,
                rejectionTags: selectedTags.map(tag => ({ id: tag.value, tagName: tag.label }))
            } : row));
            fetchStats();
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update tags'));
        } finally {
            setRowUpdating(updateKey, false);
        }
    };

    const handleRowRejectionTagsChange = (rowId, selectedOptions) => {
        const selectedTags = selectedOptions || [];
        setRowRejectionTags(prev => ({ ...prev, [rowId]: selectedTags }));
        saveRowRejectionTags(rowId, selectedTags);
    };

    const handleCreateRejectionTag = async (rowId, tagName) => {
        const trimmedTagName = (tagName || '').trim();
        if (!trimmedTagName) return;

        const updateKey = `${rowId}-rejection-tags`;
        setRowUpdating(updateKey, true);
        try {
            const response = await createNTCPremiumJobRejectionTag(adminId, { tagName: trimmedTagName });
            if (!response || response.status !== 'success') throw response;

            const data = response.data || {};
            const createdTag = data.tag || data;
            const createdOption = formatTagOption(createdTag);
            const nextOption = createdOption.label
                ? createdOption
                : { value: trimmedTagName, label: trimmedTagName, usageCount: 0 };

            setRejectionTagOptions(prev => mergeTagOptions([...prev, nextOption]));
            const nextSelectedTags = [...(rowRejectionTags[rowId] || []), nextOption];
            setRowRejectionTags(prev => ({ ...prev, [rowId]: nextSelectedTags }));
            await saveRowRejectionTags(rowId, nextSelectedTags);
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create tag'));
        } finally {
            setRowUpdating(updateKey, false);
        }
    };

    const handleRowAssigneeChange = async (rowId, value) => {
        const updateKey = `${rowId}-assignee`;
        setRowUpdating(updateKey, true);
        try {
            const response = await updateNTCPremiumJob(adminId, rowId, { assignedTo: value || null });
            if (!response || response.status !== 'success') throw response;
            setRows(prev => prev.map(row => row.id === rowId ? {
                ...row,
                assignedTo: value ? Number(value) : null,
                assignedToName: value ? assigneeNameMap[value] || '' : ''
            } : row));
            toast.success('Assignee updated successfully');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update assignee'));
        } finally {
            setRowUpdating(updateKey, false);
        }
    };

    const handleCommentSave = async (rowId, comment) => {
        const updateKey = `${rowId}-comment`;
        setRowUpdating(updateKey, true);
        try {
            const response = await updateNTCPremiumJob(adminId, rowId, { comment });
            if (!response || response.status !== 'success') throw response;
            setRows(prev => prev.map(r => r.id === rowId ? { ...r, comment } : r));
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to save comment'));
        } finally {
            setRowUpdating(updateKey, false);
        }
    };

    const onError = (message) => toast.error(message);

    const countRange = formatCountRange(pageNumber, rows.length, totalCount);
    const totalTagged = visibleMetricTags.reduce((sum, tag) => sum + (tag.usageCount || 0), 0);

    const getStatusCount = (key) => (rejectionStats.statusCounts || {})[key] || 0;

    if (!hasAccess && adminId) {
        return <div className="ntc-premium-jobs__restricted">You do not have access to this page.</div>;
    }

    return (
        <div className="ntc-premium-jobs">
            <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnVisibilityChange
                draggable={false}
                pauseOnHover
            />

            {/* Stats Modal */}
            <Modal open={statsModalOpen} onClose={() => setStatsModalOpen(false)} size="small">
                <div className="ntc-premium-jobs__stats-modal-header">
                    <h3>Premium Job Stats</h3>
                    <button className="ntc-premium-jobs__stats-modal-close" onClick={() => setStatsModalOpen(false)}>✕</button>
                </div>
                <Modal.Content>
                    <div className="ntc-premium-jobs__stats-grid">
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--total">
                            <div className="ntc-premium-jobs__stats-card-value">{totalCount || 0}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Total Jobs</div>
                        </div>
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--posted">
                            <div className="ntc-premium-jobs__stats-card-value">{rejectionStats.totalJobsPosted || 0}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Posted</div>
                        </div>
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--tagged">
                            <div className="ntc-premium-jobs__stats-card-value">{totalTagged}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Tagged</div>
                        </div>
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--published">
                            <div className="ntc-premium-jobs__stats-card-value">{getStatusCount('published')}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Published</div>
                        </div>
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--not-posted">
                            <div className="ntc-premium-jobs__stats-card-value">{getStatusCount('not-posted')}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Not Posted</div>
                        </div>
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--pending">
                            <div className="ntc-premium-jobs__stats-card-value">{getStatusCount('pending')}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Pending</div>
                        </div>
                        <div className="ntc-premium-jobs__stats-card ntc-premium-jobs__stats-card--rejected">
                            <div className="ntc-premium-jobs__stats-card-value">{getStatusCount('rejected')}</div>
                            <div className="ntc-premium-jobs__stats-card-label">Rejected</div>
                        </div>
                    </div>
                    {Object.keys(rejectionStats.statusCounts || {}).length > 0 && (
                        <div className="ntc-premium-jobs__stats-tags-section" style={{ marginBottom: 16 }}>
                            <div className="ntc-premium-jobs__stats-tags-title">Publish Status</div>
                            <div className="ntc-premium-jobs__stats-status-rows">
                                {Object.entries(rejectionStats.statusCounts).map(([status, count]) => (
                                    <div key={status} className="ntc-premium-jobs__stats-status-row">
                                        <span className={`ntc-premium-jobs__status-badge ntc-premium-jobs__status-badge--${status}`}>
                                            {STATUS_BADGE_LABELS[status] || status}
                                        </span>
                                        <span className="ntc-premium-jobs__stats-status-count">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {visibleMetricTags.length > 0 && (
                        <div className="ntc-premium-jobs__stats-tags-section">
                            <div className="ntc-premium-jobs__stats-tags-title">Tag Statistics</div>
                            <div className="ntc-premium-jobs__stats-tags">
                                {visibleMetricTags.map(tag => (
                                    <span key={tag.value} className="ntc-premium-jobs__stats-tag">
                                        {tag.label}: <strong>{tag.usageCount}</strong>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </Modal.Content>
            </Modal>

            {/* Upload CSV modal */}
            <Modal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} size="small">
                <Modal.Header>Upload CSV</Modal.Header>
                <Modal.Content>
                    <p>
                        Upload the latest CSV for <strong>{CURRENT_PLATFORM}</strong>.
                        Only jobs matching this platform are stored.
                    </p>
                    <DragAndDrop
                        formats={['csv', 'xlsx']}
                        onError={onError}
                        onUpload={handleUpload}
                        selectedFile={selectedFile}
                        setSelectedFile={setSelectedFile}
                    />
                    {uploading && <div className="ntc-premium-jobs__upload-status">Uploading CSV...</div>}
                </Modal.Content>
                <Modal.Actions>
                    <Button skin="light" text="Cancel" clickButton={() => setUploadModalOpen(false)} />
                </Modal.Actions>
            </Modal>

            <div className="page-heading ntc-premium-jobs__page-heading">
                <Heading text="NTC Premium Jobs" />
                <div className="ntc-premium-jobs__page-heading-actions">
                    <Button skin="light" text="Stats" clickButton={() => setStatsModalOpen(true)} />
                    {isManager && (
                        <Button skin="dark" text="Upload CSV" clickButton={() => setUploadModalOpen(true)} />
                    )}
                </div>
            </div>

            <ButtonContainer heading="Filters">
                <li>
                    <Dropdown
                        data={branchOptions}
                        defaultValue={branch}
                        dropdownChange={handleBranchChange}
                    />
                </li>
                <li>
                    <input
                        className="ntc-premium-jobs__filter-input"
                        type="text"
                        placeholder="Search by Job ID"
                        value={jobId}
                        onChange={handleJobIdChange}
                        onKeyDown={handleJobIdSearch}
                    />
                </li>
                <li>
                    <Select
                        classNamePrefix="ntc-cust-id-select"
                        options={custIdOptions}
                        placeholder="Filter by Cust ID"
                        isSearchable
                        isClearable
                        isMulti
                        hideSelectedOptions
                        styles={custIdSelectStyles}
                        value={custIdFilter}
                        onChange={handleCustIdFilterChange}
                        menuPortalTarget={getSelectMenuPortalTarget()}
                        menuPosition="fixed"
                        noOptionsMessage={() => 'No cust IDs available'}
                    />
                </li>
                {isManager && (
                    <li>
                        <Dropdown
                            data={assigneeOptions}
                            defaultValue={assignedTo}
                            dropdownChange={handleAssignedToFilterChange}
                        />
                    </li>
                )}
                <li>
                    <Dropdown
                        data={JOB_STATUS_OPTIONS}
                        defaultValue={jobStatus}
                        dropdownChange={handleJobStatusChange}
                    />
                </li>
                <li>
                    <Dropdown
                        data={ICP_OPTIONS}
                        defaultValue={icp}
                        dropdownChange={handleIcpChange}
                    />
                </li>
                <li className="ntc-premium-jobs__date-filter">
                    <span className="ntc-premium-jobs__date-label">Post Date</span>
                    <input
                        className="ntc-premium-jobs__filter-input ntc-premium-jobs__date-input"
                        type="date"
                        value={postDateFrom}
                        onChange={handlePostDateFromChange}
                    />
                    <span className="ntc-premium-jobs__date-sep">–</span>
                    <input
                        className="ntc-premium-jobs__filter-input ntc-premium-jobs__date-input"
                        type="date"
                        value={postDateTo}
                        onChange={handlePostDateToChange}
                    />
                </li>
                <li>
                    <Select
                        className="ntc-premium-jobs__tag-filter"
                        classNamePrefix="ntc-premium-jobs-select"
                        options={rejectionTagOptions}
                        placeholder="Filter by Tags"
                        isSearchable
                        isClearable
                        isMulti
                        hideSelectedOptions
                        styles={tagSelectStyles}
                        value={rejectionTagFilter}
                        onChange={handleRejectionTagFilterChange}
                        menuPortalTarget={getSelectMenuPortalTarget()}
                        menuPosition="fixed"
                    />
                </li>
                <li className="ntc-premium-jobs__filter-action-btns">
                    <Button
                        skin="dark"
                        text="Search"
                        clickButton={() => fetchList(1, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo)}
                    />
                    <Button
                        skin="dark"
                        text="Refresh"
                        clickButton={() => fetchList(pageNumber, branch, jobId, assignedTo, rejectionTagFilter, custIdFilter, jobStatus, postDateFrom, postDateTo)}
                    />
                    <Button
                        skin="light"
                        text="Clear Filters"
                        clickButton={handleClearFilters}
                    />
                </li>
            </ButtonContainer>

            <div className="ntc-premium-jobs__table-actions">
                <div className="ntc-premium-jobs__bulk-actions-inline">
                    {isManager && (
                        <>
                            <Checkbox
                                checked={selectAllMode || (rows.length > 0 && checkedIds.length === rows.length)}
                                indeterminate={!selectAllMode && checkedIds.length > 0 && checkedIds.length < rows.length}
                                onChange={handleSelectAll}
                                label={selectAllMode ? `All ${totalCount} jobs selected` : 'Select All'}
                            />
                            {(selectAllMode || checkedIds.length > 0) && (
                                <select
                                    className="ntc-premium-jobs__bulk-assign-select"
                                    value={bulkAssignTo}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setBulkAssignTo(val);
                                        if (val) handleBulkAssign(val);
                                    }}
                                >
                                    <option value="">Assign to…</option>
                                    {assignees.map(item => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            )}
                        </>
                    )}
                </div>
                <div className="ntc-premium-jobs__pagination-row">
                    <Pagination
                        totalItemsCount={totalCount}
                        content={PAGE_CONTENT}
                        pageNumber={pageNumber}
                        handlePageChange={handlePageChange}
                    />
                    <CountDisplay start={countRange.start} end={countRange.end} total={totalCount} />
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="ntc-premium-jobs__table-wrap">
                    <Table fixed selectable className="ntc-premium-jobs__table">
                        <Table.Header>
                            <Table.Row>
                                {isManager && <Table.HeaderCell className="ntc-premium-jobs__check-cell" />}
                                <Table.HeaderCell className="ntc-premium-jobs__company-cell">Company Details</Table.HeaderCell>
                                <Table.HeaderCell className="ntc-premium-jobs__details-cell">Job Details</Table.HeaderCell>
                                <Table.HeaderCell className="ntc-premium-jobs__postdate-cell">Post Date</Table.HeaderCell>
                                <Table.HeaderCell className="ntc-premium-jobs__tags-cell">Tags</Table.HeaderCell>
                                <Table.HeaderCell className="ntc-premium-jobs__actions-cell">Actions</Table.HeaderCell>
                                <Table.HeaderCell className="ntc-premium-jobs__status-cell">Status</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {rows.length ? rows.map(row => (
                                <Table.Row key={row.id}>
                                    {isManager && (
                                        <Table.Cell className="ntc-premium-jobs__check-cell">
                                            <Checkbox
                                                checked={checkedIds.includes(row.id)}
                                                onChange={() => handleCheckboxChange(row.id)}
                                            />
                                        </Table.Cell>
                                    )}
                                    <Table.Cell className="ntc-premium-jobs__company-cell">
                                        <div className="ntc-premium-jobs__cust-id-primary">{row.custId || 'N/A'}</div>
                                        {renderLabelValue('Company', row.companyName)}
                                        {renderLabelValue('Branch', row.branch)}
                                        {renderLabelValue('NTC Status', row.ntcJobStatus)}
                                    </Table.Cell>
                                    <Table.Cell className="ntc-premium-jobs__details-cell">
                                        <div className="ntc-premium-jobs__jobid-row">
                                            <span className="ntc-premium-jobs__jobid-text">#{row.jobId}</span>
                                            {row.jobLink && (
                                                <a href={row.jobLink} target="_blank" rel="noreferrer" className="ntc-premium-jobs__job-link-icon" title="Open source link">&#128279;</a>
                                            )}
                                            {renderPriorityBadge(row.priority)}
                                            {renderIcpBadge(row.icp)}
                                        </div>
                                        <div className="ntc-premium-jobs__title">{row.title || 'N/A'}</div>
                                        {renderLabelValue('CTC', row.ctc)}
                                        {renderLabelValue('Experience', row.experience)}
                                        {renderLabelValue('Location', row.jobLocation)}
                                        {renderLabelValue('Relevant Applies', row.relevantAppliesOnNaukri)}
                                    </Table.Cell>
                                    <Table.Cell className="ntc-premium-jobs__postdate-cell">
                                        {row.jobPostingDate || '—'}
                                    </Table.Cell>
                                    <Table.Cell className="ntc-premium-jobs__tags-cell">
                                        <Creatable
                                            className="ntc-premium-jobs__tags-select"
                                            classNamePrefix="ntc-premium-jobs-select"
                                            options={rejectionTagOptions}
                                            placeholder={isManager ? "Search or add tags" : "Search tags"}
                                            formatCreateLabel={isManager ? (inputValue => `Add "${inputValue}"`) : undefined}
                                            isValidNewOption={isManager ? undefined : () => false}
                                            isSearchable
                                            isClearable
                                            isMulti
                                            hideSelectedOptions
                                            menuPortalTarget={getSelectMenuPortalTarget()}
                                            menuPosition="fixed"
                                            styles={inlineTagSelectStyles}
                                            value={rowRejectionTags[row.id] || []}
                                            onChange={(selectedOptions) => handleRowRejectionTagsChange(row.id, selectedOptions)}
                                            onCreateOption={isManager ? ((inputValue) => handleCreateRejectionTag(row.id, inputValue)) : undefined}
                                            isDisabled={!!rowUpdateMap[`${row.id}-rejection-tags`]}
                                        />
                                        {rowUpdateMap[`${row.id}-rejection-tags`] && (
                                            <div className="ntc-premium-jobs__inline-status">Saving...</div>
                                        )}
                                        <CommentInput
                                            initialValue={row.comment || ''}
                                            onSave={(value) => handleCommentSave(row.id, value)}
                                            disabled={!!rowUpdateMap[`${row.id}-comment`]}
                                        />
                                        {rowUpdateMap[`${row.id}-comment`] && (
                                            <div className="ntc-premium-jobs__inline-status">Saving...</div>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell className="ntc-premium-jobs__actions-cell">
                                        <div className="ntc-premium-jobs__actions">
                                            {isManager && (
                                                <div className="ntc-premium-jobs__assignee-container">
                                                    <span className="ntc-premium-jobs__assignee-label">Assign To</span>
                                                    <Select
                                                        options={assignees.map(item => ({ value: item.id, label: item.name }))}
                                                        value={row.assignedTo ? { value: row.assignedTo, label: row.assignedToName || assigneeNameMap[row.assignedTo] || '' } : null}
                                                        placeholder="Unassigned"
                                                        onChange={(option) => handleRowAssigneeChange(row.id, option ? option.value : null)}
                                                        menuPortalTarget={getSelectMenuPortalTarget()}
                                                        menuPosition="fixed"
                                                        styles={assigneeSelectStyles}
                                                    />
                                                </div>
                                            )}
                                            {row.isPosted ? (
                                                <a
                                                    href={`/editjob/${row.metaId}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="ntc-premium-jobs__posted-badge"
                                                >Posted ({row.metaId})</a>
                                            ) : (
                                                <Button
                                                    skin="dark"
                                                    text="Post Job"
                                                    clickButton={() => handlePostJob(row)}
                                                />
                                            )}
                                        </div>
                                    </Table.Cell>
                                    <Table.Cell className="ntc-premium-jobs__status-cell">
                                        {renderJobPublishStatusBadge(row.jobPublishStatus)}
                                    </Table.Cell>
                                </Table.Row>
                            )) : (
                                <Table.Row>
                                    <Table.Cell colSpan={isManager ? 7 : 6} className="ntc-premium-jobs__empty">
                                        No records found
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </div>
            )}
        </div>
    );
};

export default NTCPremiumJobs;
